import { Injectable } from '@angular/core';
import { FeatureState } from '@modeso/aumico-lib-core-fe';
import { DocumentDto, ExportTasksSectionExcelData, TaskState } from '@modeso/aumico-lib-cpmm-fe';
import { ExportTasksFilterState } from '@modeso/aumico-lib-cpmm-fe/enums/export-tasks-filter.enum';
import { WorkflowArea } from '@modeso/aumico-lib-cpmm-fe/enums/workflow-area.enum';
import { Store, select } from '@ngrx/store';
import Debug from 'debug';
import { financialClosingProcessItemDocument } from 'projects/aumico-project-reports/src/app/microservices/ms-annual-reports/financial-closing-process/domain/reducers/fcp.actions';
import { Observable } from 'rxjs';
import { DocumentModel } from '../../../../aumico-lib-cpmm-fe/src/lib/interfaces/document.interface';
import * as fromDocumentsActions from '../actions/workflow-documents.actions';
import { ITasksExportTableHeaders } from '../dtos/tasks-export-table-headers.dto';
import { selectAllDocuments } from '../reducer/workflow-documents.reducer';
import { InventoryData } from '../dtos/inventory-data.dto';
import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe';

const debug = Debug('aumico:aumico-lib-core-fe:WorkflowDocumentsProvider');

@Injectable({
    providedIn: 'root',
})
export class WorkflowDocumentsProvider {
    constructor(private readonly store: Store<FeatureState>) {
        debug('WorkflowDocumentsProvider constructed');
    }

    public dispatchLoadDocumentsForTask(taskId: string): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.loadTaskDocuments({ taskId }));
    }

    public dispatchUploadDocument(taskId: string, files: File[], document: Partial<DocumentModel>): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.uploadDocument({ taskId, files, document }));
    }

    public dispatchDeleteDocument(documentId: string, projectId: string): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.deleteDocument({ documentId, projectId }));
    }

    public dispatchUpdateDocumentMetadata(document: Partial<DocumentModel>): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.updateDocumentMetadata({ document }));
    }

    public dispatchExportDocuments(
        projectId: string,
        companyName: string,
        balanceSheetDate: string,
        workFlowAreas: WorkflowArea[],
        inventoryData: InventoryData,
    ): void {
        this.store.dispatch(
            fromDocumentsActions.DocumentsActions.exportProjectDocuments({
                projectId,
                companyName,
                balanceSheetDate,
                workFlowAreas,
                inventoryData,
            }),
        );
    }

    public getDocumentsForTask$(): Observable<DocumentDto[]> {
        return this.store.pipe(select(selectAllDocuments));
    }

    public dispatchExportTasksAsExcel(
        tableHeaders: ITasksExportTableHeaders,
        data: ExportTasksSectionExcelData[],
        fileName: string,
        tasksFilter: (TaskState | ExportTasksFilterState)[],
        tableTitle: string,
        organization: GetOrganizationDto,
    ): void {
        this.store.dispatch(
            fromDocumentsActions.DocumentsActions.exportTasksExcel({
                payload: { tableHeaders, data, fileName, tasksFilter, tableTitle, organization },
            }),
        );
    }

    public dispatchCheckPreviewStatus(fileId: string): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.checkPreviewStatus({ fileId }));
    }

    public dispatchRequestPreviewGeneration(fileId: string): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.requestPreviewGeneration({ fileId }));
    }

    public dispatchLoadPreviewUrl(fileId: string): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.loadPreviewUrl({ fileId }));
    }

    public dispatchOpenFile(fileId: string): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.openFile({ fileId }));
    }

    public dispatchRevokeFileUrl(fileId: string): void {
        this.store.dispatch(fromDocumentsActions.DocumentsActions.revokeFileUrl({ fileId }));
    }

    public dispatchDownloadFile(documentId: string, documentName: string): void {
        this.store.dispatch(financialClosingProcessItemDocument.download({ documentId, documentName }));
    }
}
